import { Box, Container } from '@hover/blueprint';
import React, { useState } from 'react';
import styled from '@emotion/styled';
import { Settings } from 'react-slick';
import { SlickSliderContainer } from '../../ui';
import { INSURANCE_CUSTOMERS_QUOTE_DATA } from '../../../constants';
import DesktopSlide from './DesktopSlide';
import MobileSlide from './MobileSlide';
import { ScreenReaderText, CarouselArrow } from '../../common';

const StyledSlider = styled(SlickSliderContainer)`
  .slick-dots li button:before {
    color: #e9e9e9;
  }

  .slick-slide div div {
    display: flex !important;
  }

  .slick-dots {
    bottom: -40px;
  }

  @media (max-width: 834px) {
    .slick-dots {
      bottom: -50px;
    }
  }
`;

const settings: Settings = {
  dots: true,
  infinite: true,
  slidesToShow: 1,
  slidesToScroll: 1,
  prevArrow: <CarouselArrow isPrevious />,
  nextArrow: <CarouselArrow />,
  appendDots: (dots: JSX.Element[]) => (
    <ul aria-label="Jump to slides using bullets">{dots}</ul>
  ),
};

const InsuranceCustomerQuote: React.FC = () => {
  const [currentSlide, setCurrentSlide] = useState<number>(1);
  const ActiveSlide = () => (
    <ScreenReaderText
      content={`Slide ${currentSlide} of ${INSURANCE_CUSTOMERS_QUOTE_DATA.length}`}
    />
  );
  return (
    <Box
      mt={{ base: 700, tablet: 1000, desktop: 0 }}
      bg={{ base: 'neutral.0', desktop: 'brandTan.100' }}
      pt={{ base: 0, desktop: 1000 }}
      px={{ base: 500, tablet: '56px', desktop: 0 }}
      pb={{ base: 600, desktop: 0 }}
      w="100%"
      role="region"
      aria-label="Slideshow"
    >
      <Container
        pt={{ base: 700, desktop: 0 }}
        pb={{ base: '82px', tablet: 1000, desktop: 1100 }}
        maxW="1200px"
        bg="brandTan.100"
        borderRadius={{ base: '8px', desktop: 0 }}
        shadow={{
          base: 'distance300',
          desktop: 'none',
        }}
        aria-live="polite"
      >
        <ScreenReaderText
          content="Carousel with 2 slides shown at a time.Use the Previous and Next buttons
        to navigate, or the slide buttons at the bottom to jump to slides."
        />
        <Box d={{ base: 'none', desktop: 'block' }} w="100%">
          <ActiveSlide />
          <StyledSlider
            {...settings}
            beforeChange={(_, next) => setCurrentSlide(next + 1)}
          >
            {INSURANCE_CUSTOMERS_QUOTE_DATA.map(
              ({ id, logo, image, header, quote, role, name }) => (
                <DesktopSlide
                  id={id}
                  logo={logo}
                  image={image}
                  header={header}
                  role={role}
                  key={id}
                  quote={quote}
                  name={name}
                  aria-hidden={currentSlide !== id}
                />
              ),
            )}
          </StyledSlider>
        </Box>
        <Box d={{ base: 'block', desktop: 'none' }} w="100%">
          <ActiveSlide />
          <StyledSlider
            {...settings}
            beforeChange={(_, next) => setCurrentSlide(next + 1)}
          >
            {INSURANCE_CUSTOMERS_QUOTE_DATA.map(
              ({ id, logo, image, header, quote, role, name }) => (
                <MobileSlide
                  id={id}
                  logo={logo}
                  image={image}
                  header={header}
                  role={role}
                  key={id}
                  quote={quote}
                  name={name}
                  currentSlide={currentSlide}
                />
              ),
            )}
          </StyledSlider>
        </Box>
      </Container>
    </Box>
  );
};

export default InsuranceCustomerQuote;
